class Modal {

	/**
	 * This class is a simple modal implementation that is designed to be extended
	 * - also relies on the proper css being included - see components/_modal.scss
	 *
	 * @param {string} content - the html content of the modal
	 * @param {object} opts
	 * @param {string} opts.bodyClass - the class to modify the body node on open/close (default: fixed)
	 * @param {string} opts.closeIcon - the markup for the close icon (default: &times;)
	 * @param {string} opts.closeText - the markup for the close text (default: close)
	 * @param {string} opts.focusable - the selector for finding the first focusable element within the modal after opened (default: a[href],button,input,select,textarea,[contenteditable],[tabindex]:not([tabindex="-1"]))
	 * @param {string} opts.modifier - a BEM modifier to apply to the default template classes (default: '')
	 * @param {function} opts.onclose - the callback for the close method (default: null)
	 * @param {function} opts.onopen - the callback for the open method (default: null)
	 * @param {int|float} opts.opacity - the opacity of the overlay - not set by default (default: null)
	 * @param {HTMLElement} opts.target - the html node used to open the modal - used in measuring the animation and returning focus on close (default: null)
	 */
	constructor(content, opts = {}) {
		this.content = content;

		this.opts = Object.assign({
			bodyClass: 'fixed',
			closeIcon: '&times;',
			closeText: 'Close',
			focusable: 'a[href],button,input,select,textarea,[contenteditable],[tabindex]:not([tabindex="-1"])',
			modifier: '',
			onclose: null,
			onopen: null,
			opacity: null,
			target: null,
		}, opts);

		this.classNames = {
			modal     : 'modal',
			overlay   : 'modal__overlay',
			frame     : 'modal__frame',
			actions   : 'modal__actions',
			action    : 'modal__action',
			content   : 'modal__content',
			actionText: 'modal__action-text',
			actionIcon: 'modal__action-icon',
		};

		this.classNames.visible = `${this.classNames.modal}--visible`;

		if ( this.opts.modifier ) {
			Object.keys(this.classNames).forEach((key) => {
				if ( this.classNames[key].indexOf('--') === -1 ) {
					this.classNames[key] += ` ${this.classNames[key]}--${this.opts.modifier}`
				}
			});
		}

		this.opened = false;

		this.node = document.createElement('div');
		this.node.className = this.classNames.modal;

		this.node.innerHTML = this.template(this.content);

		this.handleEsc = (e) => {
			if ( this.opened && e.key === 'Escape' ) {
				this.close();
			}
		}

		this.handleClose = (e) => {
			if ( ! this.opened ) {
				return;
			}

			let node = e.target;

			// close on overlay click
			switch( true ) {
				case node.classList.contains('modal'):
				case node.classList.contains('modal__overlay'):
					this.close();
				break;
			}

			// close on modal__close button
			while( node && node.parentNode ) {
				if ( node.classList.contains('modal__action--close') ) {
					this.close();
					break;
				}

				node = node.parentNode;
			}
		}
	}

	/**
	 * Return the html template for the modal
	 *
	 * @param {string} content
	 * @return {string}
	 */
	template(content) {
		return `
			<div class="${this.classNames.overlay}" style="${typeof this.opts.opacity === 'number' ? Math.min( this.opts.opacity, 1 ) : ''}"></div>
			<div class="${this.classNames.frame}">
				<div class="${this.classNames.actions}">
					<button type="button" class="${this.classNames.action} modal__action--close">
					<span class="${this.classNames.actionText}">${this.opts.closeText}</span>
					<span class="${this.classNames.actionIcon}">${this.opts.closeIcon}</span>
					</button>
				</div>
				<div class="${this.classNames.content}">
					${content}
				</div>
			</div>
		`.trim();
	}

	/**
	 * Open the modal
	 */
	open() {
		// internal flag
		this.opened = true;

		// add the node as invisible to the DOM
		document.body.appendChild(this.node);

		// listen for click/esc
		document.addEventListener('keyup', this.handleEsc);
		this.node.addEventListener('click', this.handleClose);

		// change the transformOrigin if we have a target node
		if ( this.opts.target ) {
			let rect = this.opts.target.getBoundingClientRect();
			this.node.style.transformOrigin = `${rect.left + ( rect.width / 2 )}px ${rect.top + ( rect.height / 2)}px`;
		}

		// call callbacks and fire custom events
		if ( typeof this.opts.onopen === 'function' ) {
			this.opts.onopen(this);
		}
		this.node.dispatchEvent( new CustomEvent('modal:open') );

		// set a body class
		document.body.classList.add(this.opts.bodyClass);

		// focus the first focusable item inside the modal if found
		if (this.opts.focusable) {
			let focusable = this.node.querySelector(this.opts.focusable);
			if ( focusable ) {
				focusable.focus();
			}
		}

		// delay adding the visible class to ensure the animation origin
		setTimeout( () => {
			this.node.classList.add('modal--visible');
		}, 100);
	}

	close() {
		// internal flag
		this.opened = false;

		// remove click/esc event listeners
		document.removeEventListener('keyup', this.handleEsc);
		this.node.removeEventListener('click', this.handleClose);

		// call callbacks and fire custom events
		if ( typeof this.opts.onclose === 'function' ) {
			this.opts.onclose(this);
		}
		this.node.dispatchEvent( new CustomEvent('modal:close') );

		// remove and reset the node on transition end
		let transitionend = () => {
			this.node.style.transformOrigin = '';
			if ( this.node.parentNode ) {
				this.node.parentNode.removeChild(this.node);
			}
			this.node.removeEventListener('transitionend', transitionend);
		}
		this.node.addEventListener('transitionend', transitionend);

		// remove the visible class to trigger transition
		this.node.classList.remove(this.classNames.visible);

		// reset the body class
		document.body.classList.remove(this.opts.bodyClass);

		// return focus to the target on close
		if ( this.opts.target instanceof HTMLElement ) {
			this.opts.target.focus();
		}
	}
}

export default Modal;
